import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Clock, LayoutGrid, Leaf } from 'lucide-react';
import { Section, Container } from '../../ui';
import styles from './Lab.module.css';

interface StatDef {
  target: number;
  decimals: number;
  prefix: string;
  suffix: string;
  labelKey: string;
  iconType: 'star' | 'clock' | 'grid' | 'leaf';
}

const stats: StatDef[] = [
  { target: 9.2, decimals: 1, prefix: '', suffix: '/10', labelKey: 'lab.stats.0.label', iconType: 'star' },
  { target: 4, decimals: 0, prefix: '< ', suffix: '%', labelKey: 'lab.stats.1.label', iconType: 'clock' },
  { target: 55000, decimals: 0, prefix: '+', suffix: '', labelKey: 'lab.stats.2.label', iconType: 'grid' },
  { target: 25, decimals: 0, prefix: '~', suffix: ' kg', labelKey: 'lab.stats.3.label', iconType: 'leaf' },
];

function formatNumber(value: number, decimals: number, lang: string): string {
  if (decimals > 0) {
    return lang === 'en'
      ? value.toFixed(decimals)
      : value.toFixed(decimals).replace('.', ',');
  }
  return Math.round(value).toLocaleString(lang === 'en' ? 'en-US' : 'fr-FR');
}

function useCounter(target: number, decimals: number, delay: number, lang: string) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const duration = 1500;
      const startTime = performance.now();
      let rafId: number;

      const animate = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(target * eased);
        if (progress < 1) {
          rafId = requestAnimationFrame(animate);
        } else {
          setValue(target);
        }
      };

      rafId = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(rafId);
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, delay]);

  return formatNumber(value, decimals, lang);
}

const statIcons: Record<StatDef['iconType'], React.ReactNode> = {
  star: <Star size={18} strokeWidth={1.5} />,
  clock: <Clock size={18} strokeWidth={1.5} />,
  grid: <LayoutGrid size={18} strokeWidth={1.5} />,
  leaf: <Leaf size={18} strokeWidth={1.5} />,
};

function StatItem({ stat, index, lang }: { stat: StatDef; index: number; lang: string }) {
  const { t } = useTranslation('home');
  const delay = index * 150;
  const display = useCounter(stat.target, stat.decimals, delay, lang);

  const iconClass = {
    star: styles.iconStar,
    clock: styles.iconClock,
    grid: styles.iconGrid,
    leaf: styles.iconLeaf,
  }[stat.iconType];

  return (
    <div className={styles.statItem}>
      <div className={`${styles.statIcon} ${iconClass}`}>
        {statIcons[stat.iconType]}
      </div>
      <div className={styles.statInfo}>
        <span className={styles.statValue}>
          {stat.prefix}{display}{stat.suffix}
        </span>
        <span className={styles.statLabel}>{t(stat.labelKey)}</span>
      </div>
    </div>
  );
}

export function Lab() {
  const { t, i18n } = useTranslation('home');

  return (
    <Section variant="dark" className={styles.section}>
      <Container>
        <div className={styles.labo}>
          <div className={styles.content}>
            <span className={styles.badge}>
              <svg className={styles.badgeDot} viewBox="0 0 16 16">
                <circle className={styles.dotPulse} cx="8" cy="8" r="4" />
                <circle className={styles.dotCore} cx="8" cy="8" r="3" />
              </svg>
              {t('lab.badge')}
            </span>

            <h2 className={styles.title}>{t('lab.title')}</h2>

            <p className={styles.text}>
              {t('lab.textRaw').split('QuelBonPlan.fr').map((part, i, arr) =>
                i < arr.length - 1 ? (
                  <span key={i}>
                    {part}
                    <a href="https://quelbonplan.fr" target="_blank" rel="noopener noreferrer">
                      <strong>QuelBonPlan.fr</strong>
                    </a>
                  </span>
                ) : (
                  <span key={i}>{part}</span>
                )
              )}
            </p>

            <div className={styles.benefit}>
              <p>{t('lab.benefit')}</p>
            </div>

            <div className={styles.statsGrid}>
              {stats.map((stat, i) => (
                <StatItem key={stat.labelKey} stat={stat} index={i} lang={i18n.language} />
              ))}
            </div>

            <div className={styles.marketQuote}>
              <p className={styles.marketQuoteText}>{t('lab.marketQuote')}</p>
              <span className={styles.marketQuoteSource}>{t('lab.marketQuoteSource')}</span>
            </div>
          </div>

          <div className={styles.visual}>
            <div className={styles.laptop}>
              <div className={styles.laptopScreen}>
                <div className={styles.laptopTopbar}>
                  <span className={styles.topbarDot} />
                  <span className={styles.topbarDot} />
                  <span className={styles.topbarDot} />
                  <div className={styles.urlBar}>
                    <svg viewBox="0 0 12 12" fill="none" stroke="#8b9099" strokeWidth="1.5">
                      <rect x="1" y="1" width="10" height="10" rx="2" />
                    </svg>
                    quelbonplan.fr
                  </div>
                </div>
                <div className={styles.laptopViewport}>
                  <video autoPlay muted loop playsInline tabIndex={-1} preload="none" poster="/images/gif_catalogue-poster.webp">
                    <source src="/images/gif_catalogue.mp4" type="video/mp4" />
                  </video>
                </div>
              </div>
              <div className={styles.laptopBase} />
              <div className={styles.laptopShadow} />
            </div>

            <div className={styles.floatBadge}>
              <div className={styles.floatBadgeContent}>
                <div className={styles.floatBadgeIcon}>
                  <Star size={16} strokeWidth={1.5} />
                </div>
                <div className={styles.floatBadgeText}>
                  <span className={styles.floatBadgeTitle}>{t('lab.floatBadge.title')}</span>
                  <span className={styles.floatBadgeSubtitle}>{t('lab.floatBadge.subtitle')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
